import { FC } from 'react';

import { StorifymeSnap } from '@sparkplug/lib';

import { useUpdateSnapMutation } from '@features/spark-snaps/mutations';
import { useSnapQuery } from '@features/spark-snaps/queries';

import Button from '@components/buttons/Button';
import Form from '@components/form/Form';
import { CircleMinusIcon, Edit as EditIcon } from '@components/icons';
import CalloutMessage from '@components/layout/CalloutMessage';
import Drawer from '@components/overlays/Drawer';

import SnapPosterThumbnail from '../../SnapPosterThumbnail';

import './CreateEditSnapDrawer.scss';

const SnapNameField: FC<{ snap?: StorifymeSnap; showHelper?: boolean }> = ({
    snap,
    showHelper = false,
}) => {
    return (
        <Form.TextField
            key={snap?.id}
            required
            label="Name"
            name="snapName"
            defaultValue={snap?.name}
            helperText={showHelper ? 'Snap name is visible to participants.' : ''}
        />
    );
};

type CreateSnapDrawerContentProps = Pick<CreateSnapDrawerProps, 'onAdd' | 'onClose' | 'isTemplate'>;
const CreateSnapDrawerContent: FC<CreateSnapDrawerContentProps> = ({
    onAdd,
    onClose,
    isTemplate,
}) => {
    return (
        <Form>
            <SnapNameField showHelper={!isTemplate} />
            <div className="create-edit-snap-drawer_snap-thumbnail">
                <SnapPosterThumbnail title="Unnamed" slideCount={0} imgSrc="" />
            </div>
            <Drawer.Footer>
                <div>
                    <Button color="neutral" onClick={onClose}>
                        Cancel
                    </Button>
                    <Form.Button variant="smooth" onClick={onAdd}>
                        Continue
                    </Form.Button>
                </div>
            </Drawer.Footer>
        </Form>
    );
};

type EditSnapDrawerContentProps = Required<
    Pick<
        EditSnapDrawerProps,
        'accountId' | 'snapId' | 'onRemove' | 'onOpenInEditor' | 'onClose' | 'isTemplate'
    >
>;
const EditSnapDrawerContent: FC<EditSnapDrawerContentProps> = ({
    accountId,
    snapId,
    onClose,
    onRemove,
    onOpenInEditor,
    isTemplate,
}) => {
    const { snapIsReady, snap, snapThumbnail } = useSnapQuery(
        {
            accountId,
            snapId: snapId as number,
        },
        !!snapId,
    );

    const { updateSnapAsync } = useUpdateSnapMutation({
        accountId,
        snapId: snapId as number,
        isTemplate,
    });

    const handleUpdateSnap = async ({ snapName }: { snapName: string }) => {
        updateSnapAsync({ name: snapName });
        onClose();
    };

    if (!snapIsReady || !snap) {
        return <></>;
    }

    const slideCount = snap?.configuration?.pages?.length ?? 0;

    return (
        <Form>
            <Form.HiddenField name="snapId" value={String(snapId)} />
            <SnapNameField snap={snap} />
            <div className="create-edit-snap-drawer_snap-thumbnail">
                <SnapPosterThumbnail
                    imgSrc={snapThumbnail}
                    title={snap.name}
                    slideCount={slideCount}
                />
                <div className="create-edit-snap-drawer_snap-thumbnail_edit-button">
                    <Button
                        variant="filled"
                        startIcon={<EditIcon />}
                        onClick={() => onOpenInEditor(snapId)}
                    >
                        Edit Snap
                    </Button>
                </div>
            </div>
            <Drawer.Footer>
                <div>
                    <Button
                        color="red"
                        onClick={() => onRemove(snapId)}
                        startIcon={<CircleMinusIcon />}
                    >
                        {isTemplate ? 'Remove Template' : 'Remove Snap'}
                    </Button>
                </div>
                <div>
                    <Button color="neutral" onClick={onClose}>
                        Cancel
                    </Button>
                    <Form.Button variant="smooth" onClick={handleUpdateSnap}>
                        Save
                    </Form.Button>
                </div>
            </Drawer.Footer>
        </Form>
    );
};

interface SnapDrawerProps {
    isVisible: boolean;
    onClose: () => void;
    isTemplate?: boolean;
}
interface EditSnapDrawerProps extends SnapDrawerProps {
    snapId: number;
    accountId: string;
    onOpenInEditor: (id: number) => void;
    onRemove: (id: number) => void;
}
interface CreateSnapDrawerProps extends SnapDrawerProps {
    onAdd: ({ snapName }: { snapName: string }) => void;
}
type CreateEditSnapDrawerProps = EditSnapDrawerProps | CreateSnapDrawerProps;

const isEditSnapDrawerProps = (props: CreateEditSnapDrawerProps): props is EditSnapDrawerProps => {
    return (props as EditSnapDrawerProps).snapId !== undefined;
};

const CreateEditSnapDrawer: FC<CreateEditSnapDrawerProps> = (props) => {
    const { isVisible, onClose, isTemplate = false } = props;
    const isEditMode = isEditSnapDrawerProps(props);
    let label = 'Add Spark Snap';
    if (isTemplate) {
        label = isEditMode ? 'Edit Snap Template' : 'Create Snap Template';
    } else if (isEditMode) {
        label = 'Edit Spark Snap';
    }

    const editContent = () => {
        const { accountId, snapId, onRemove, onOpenInEditor } = props as EditSnapDrawerProps;
        return (
            <EditSnapDrawerContent
                accountId={accountId}
                snapId={snapId}
                onRemove={onRemove}
                onOpenInEditor={onOpenInEditor}
                onClose={onClose}
                isTemplate={isTemplate}
            />
        );
    };

    const createContent = () => {
        const { onAdd } = props as CreateSnapDrawerProps;
        return (
            <CreateSnapDrawerContent
                onAdd={({ snapName }) => onAdd({ snapName })}
                onClose={onClose}
                isTemplate={isTemplate}
            />
        );
    };

    return (
        <Drawer
            title={label}
            className="create-edit-snap-drawer"
            variant="right"
            isVisible={isVisible}
            onCloseHandler={onClose}
        >
            <CalloutMessage
                message={
                    isTemplate
                        ? 'Create a template to easily apply consistent styling and formatting across your Snaps on Sparks.'
                        : 'Enter a Snap name that will be visible to Spark participants, then continue to create the Snap!'
                }
            />
            {
                // Adding `isVisible` here will cause the drawer to re-render the content, clearing the form fields
                isVisible && <>{isEditMode ? editContent() : createContent()}</>
            }
        </Drawer>
    );
};

export default CreateEditSnapDrawer;
